const ENEMY_DEBOUNCE_MS = 400;
const ENEMY_ENDPOINTS: Record<EnemyField, string> = {
    [EnemyField.Number]: "/Participant/SetEnemyNumber",
    [EnemyField.HitPoints]: "/Participant/SetEnemyHitPoints",
    [EnemyField.MaxHitPoints]: "/Participant/SetEnemyMaxHitPoints"
};

// Null while the context menu is open on a group or a player rather than on one standee.
let contextEnemyId: string | null = null;

// Standees sit two to a cell pair: the first pair rides in the group's own row, the rest fill rows under it
// with the same two columns, so a standee always sits where the standee above it does.
const ENEMIES_PER_ROW = 2;

function ChunkEnemies(enemies: Enemy[]): Enemy[][] {
    const rows: Enemy[][] = [];
    for (let i = 0; i < enemies.length; i += ENEMIES_PER_ROW) {
        rows.push(enemies.slice(i, i + ENEMIES_PER_ROW));
    }

    return rows;
}

function BuildEnemyCells(group: Participant, enemies: Enemy[]): HTMLDivElement {
    const cells = document.createElement('div');
    cells.className = "enemy-cells";
    for (const enemy of enemies) {
        cells.append(BuildEnemyCell(group, enemy));
    }

    return cells;
}

// Stretched rows take the whole width, which gives the conditions room to sit side by side. Only when no pair
// rides in the group's row, because the stretched columns no longer line up with that pair.
function BuildEnemyRow(group: Participant, enemies: Enemy[], stretched: boolean): HTMLDivElement {
    const row = document.createElement('div');
    row.className = "player-row player-row--enemy";
    row.style.backgroundColor = group.primaryHexColor;
    if (group.isDead) {
        row.classList.add("player-row--dead");
    }
    if (stretched) {
        row.classList.add("player-row--enemy-stretched");
    }

    const right = document.createElement('div');
    right.className = "participant-right";
    right.append(BuildEnemyCells(group, enemies));

    if (!stretched) {
        // Takes the width of the initiative box, so the cells line up under the group's.
        const spacer = document.createElement('div');
        spacer.className = "enemy-initiative-spacer";
        right.append(spacer);
    }
    row.append(right);

    return row;
}

function BuildEnemyCell(group: Participant, enemy: Enemy): EnemyCell {
    const cell = document.createElement('div') as EnemyCell;
    cell.className = "enemy-cell";
    cell.participant = group;
    cell.enemy = enemy;
    cell.addEventListener('click', OpenEnemyContext, false);

    const isDead = enemy.hitPoints === 0;
    if (isDead) {
        cell.classList.add("enemy-cell--dead");
    }

    // A summon is known by the name its player gave it, a standee by its number.
    const label = HasTrackerValue(enemy.name) ? enemy.name : enemy.number;
    if (HasTrackerValue(label)) {
        const badge = document.createElement('span');
        badge.className = HasTrackerValue(enemy.name) ? "enemy-label__number enemy-label__number--name" : "enemy-label__number";
        badge.innerText = label;
        cell.append(badge);
    }

    const conditions = document.createElement('div');
    conditions.className = "enemy-cell__conditions";
    if (enemy.conditions.length > 0) {
        conditions.append(BuildConditionIcons(enemy.conditions));
    }
    cell.append(conditions);

    if (isDead || group.isDead) {
        cell.append(BuildDeathIcon());
    } else if (HasTrackerValue(enemy.hitPoints)) {
        cell.append(BuildHitPointBar(enemy.hitPoints, enemy.maxHitPoints));
    }

    return cell;
}

// The first pair only stays in the group's row when the name still fits beside it; a name that gets clipped
// or a row that overflows sends the pair to a row of its own.
function EnemyCellsDoNotFit(groupRow: HTMLElement): boolean {
    const name = groupRow.querySelector<HTMLElement>(".participant-initiative-name");
    return groupRow.scrollWidth > groupRow.clientWidth || (name !== null && name.scrollWidth > name.clientWidth);
}

function SortEnemies(enemies: Enemy[]): Enemy[] {
    return enemies.slice().sort((a, b) => Number(a.hitPoints === 0) - Number(b.hitPoints === 0));
}

// Set while the popup asks a monster group for the maximum its standees start with.
let enemyMaxGroup: Participant | null = null;

// How many standees wait for the answer.
let enemyMaxCount = 1;

// The group or player the context menu is open on.
let contextOwner: Participant | null = null;

interface AddEnemyOptions {
    maxHitPoints?: number;
    name?: string;
    count?: number;
}

// A player adds a summon and names it; a monster group is asked how many to add, and when it has no maximum
// yet, what its standees start with, so the DM does not have to fill that in per standee.
function RequestAddEnemy(group: Participant): void {
    if (group.role !== Role.Monster) {
        const name = window.prompt("Naam van de summon:");
        if (name !== null) {
            AddEnemy(group.nameSluggified, { name: name });
        }

        return;
    }

    const answer = window.prompt("Hoeveel enemies toevoegen?", "1");
    const count = answer === null ? 0 : parseInt(answer, 10);
    if (!(count > 0)) {
        return;
    }

    if (!(group.maxHitPoints !== null && group.maxHitPoints > 0)) {
        OpenEnemyMaxPopup(group, count);
        return;
    }

    AddEnemy(group.nameSluggified, { count: count });
}

function OpenEnemyMaxPopup(group: Participant, count: number): void {
    CloseAllPopups();
    enemyMaxGroup = group;
    enemyMaxCount = count;
    ById<HTMLElement>("enemyMaxGroup").innerText = group.name;

    const input = ById<HTMLInputElement>("enemyMaxInput");
    input.value = "";
    ById<HTMLElement>("enemyMaxDropdown").classList.add("show");
    input.focus();
}

function ConfirmEnemyMax(): void {
    const maxHitPoints = parseInt(ById<HTMLInputElement>("enemyMaxInput").value, 10);
    if (enemyMaxGroup === null || !(maxHitPoints > 0)) {
        return;
    }

    AddEnemy(enemyMaxGroup.nameSluggified, { maxHitPoints: maxHitPoints, count: enemyMaxCount });
    CloseAllPopups();
}

function AddEnemyWithoutMax(): void {
    if (enemyMaxGroup === null) {
        return;
    }

    AddEnemy(enemyMaxGroup.nameSluggified, { count: enemyMaxCount });
    CloseAllPopups();
}

function AddEnemy(nameSluggified: string, options: AddEnemyOptions = {}): void {
    const data: Record<string, string> = { slugName: nameSluggified, roomKey: RoomKey() };
    if (options.maxHitPoints !== undefined) {
        data.maxHitPoints = String(options.maxHitPoints);
    }
    if (options.name !== undefined) {
        data.name = options.name;
    }
    if (options.count !== undefined) {
        data.count = String(options.count);
    }

    PostRoundWrite("enemy:add:" + nameSluggified, "/Participant/AddEnemy", data);
}

function EnemyLabel(group: Participant, enemy: Enemy): string {
    if (HasTrackerValue(enemy.name)) {
        return group.name + " - " + enemy.name;
    }

    return HasTrackerValue(enemy.number) ? group.name + " #" + enemy.number : group.name;
}

function OpenEnemyContext(event: MouseEvent): void {
    event.stopPropagation();
    const row = event.currentTarget as EnemyCell;
    contextEnemyId = row.enemy.id;
    localStorage.setItem("nameSluggified", row.participant.nameSluggified);

    ById<HTMLElement>("applyConditionDropdown").classList.add("show");
    SetContextHeader(EnemyLabel(row.participant, row.enemy));
    SetContextValue("contextHitPoints", row.enemy.hitPoints);
    SetContextValue("contextMaxHitPoints", row.enemy.maxHitPoints);
    SetContextValue("contextEnemyNumber", row.enemy.number);
    RenderContextHitPointControls();
    RenderContextDeathToggle(row.enemy.hitPoints === 0, CanManage(row.participant));
    RenderContextMode(row.participant);
}

// One menu serves players, groups and standees; this shows the parts that apply to what was clicked.
function RenderContextMode(owner: Participant): void {
    contextOwner = owner;
    const isEnemy = contextEnemyId !== null;
    const isDungeonMaster = ParticipantRole() === Role.DungeonMaster;
    const isMonsterGroup = owner.role === Role.Monster;
    const canManage = CanManage(owner);
    const canReset = !isEnemy && canManage && !owner.isDead;
    const canAddEnemy = !isEnemy && canManage && owner.enemies.length < MaxEnemies;
    const canRemoveParticipant = !isEnemy && isDungeonMaster;
    const canRemoveEnemy = isEnemy && canManage;

    // A monster group with standees has no hit points of its own to show; its maximum is what the next
    // standee starts with.
    const maxOnly = !isEnemy && isMonsterGroup && owner.enemies.length > 0;
    ById<HTMLElement>("contextHitPointsTitle").innerText = maxOnly ? "Hit points nieuwe enemies" : "Hit points";
    ById<HTMLElement>("contextHitPointControls").classList.toggle("hit-point-controls--max-only", maxOnly);
    ById<HTMLElement>("contextAddEnemyLabel").innerText = isMonsterGroup ? "Enemy" : "Summon";

    ShowContextPart("contextExperienceSection", !isEnemy && !isMonsterGroup);
    ShowContextPart("contextArmorSection", !isEnemy && !isMonsterGroup);
    ShowContextPart("contextEnemyNumberSection", isEnemy && canManage);
    ShowContextPart("contextResetInitiative", canReset);
    ShowContextPart("contextAddEnemy", canAddEnemy);
    ShowContextPart("contextRemoveParticipant", canRemoveParticipant);
    ShowContextPart("contextRemoveEnemy", canRemoveEnemy);
    ShowContextPart("contextActionsSection", canReset || canAddEnemy || canRemoveParticipant || canRemoveEnemy);
}

function ShowContextPart(elementId: string, visible: boolean): void {
    ById<HTMLElement>(elementId).style.display = visible ? "" : "none";
}

function PostContextEnemyField(field: EnemyField, elementId: string): void {
    const nameSluggified = localStorage.getItem("nameSluggified");
    if (!nameSluggified || contextEnemyId === null) {
        return;
    }

    QueueEnemyUpdate(field, nameSluggified, contextEnemyId, ById<HTMLInputElement>(elementId).value);
}

function QueueEnemyUpdate(field: EnemyField, nameSluggified: string, enemyId: string, value: string): void {
    const key = "enemy:" + field + ":" + nameSluggified + ":" + enemyId;
    HoldWrite(key);

    Debounce(key, ENEMY_DEBOUNCE_MS, function () {
        const data: Record<string, string> = { slugName: nameSluggified, enemyId: enemyId, roomKey: RoomKey() };
        data[field] = value;

        PostRoundWrite(key, ENEMY_ENDPOINTS[field], data);
    });
}

function ContextEnemyNumberChanged(): void {
    PostContextEnemyField(EnemyField.Number, "contextEnemyNumber");
}

function ApplyEnemyCondition(nameSluggified: string, enemyId: string, conditionAsString: ConditionType): void {
    PostRoundWrite("condition:" + nameSluggified + ":" + enemyId, "/Participant/AddEnemyCondition", {
        slugName: nameSluggified,
        enemyId: enemyId,
        conditionAsString: conditionAsString,
        roomKey: RoomKey()
    });
}

function AddContextEnemy(): void {
    if (contextOwner === null) {
        return;
    }

    ById<HTMLElement>("applyConditionDropdown").classList.remove("show");
    RequestAddEnemy(contextOwner);
}

function RemoveContextEnemy(): void {
    const nameSluggified = localStorage.getItem("nameSluggified");
    if (!nameSluggified || contextEnemyId === null) {
        return;
    }

    PostRoundWrite("enemy:remove:" + nameSluggified, "/Participant/RemoveEnemy", {
        slugName: nameSluggified,
        enemyId: contextEnemyId,
        roomKey: RoomKey()
    });

    contextEnemyId = null;
    ById<HTMLElement>("applyConditionDropdown").classList.remove("show");
}
